import createEmojiRegExp from "emoji-regex";

/** Only the configured deployment's public emoji route may bypass the internal-origin block. */
export function isGlideEmojiURL(source: string, endpoint: string): boolean {
    try {
        const url = new URL(source);
        if (
            !["http:", "https:"].includes(url.protocol) ||
            url.origin !== new URL(endpoint).origin ||
            url.username !== "" ||
            url.password !== "" ||
            url.search !== "" ||
            url.hash !== ""
        )
            return false;
        const match = /^\/getEmoji\/([^/]{1,64})$/.exec(decodeURIComponent(url.pathname));
        return match !== null && createEmojiRegExp().exec(match[1])?.[0] === match[1];
    } catch {
        return false;
    }
}

/** Public image aliases point only at verified objects, never at backup manifests. */
export interface PreservedImageEntry {
    readonly sourceURL: string;
    readonly bucket: string;
    readonly name: string;
    readonly generation: string;
    readonly size: number;
    readonly md5: string;
    readonly contentType: string;
}

export function isCloudinaryURL(source: string): boolean {
    try {
        const url = new URL(source);
        const hostname = url.hostname.replace(/\.$/, "");
        return hostname === "cloudinary.com" || hostname.endsWith(".cloudinary.com");
    } catch {
        return false;
    }
}

/** Customer-owned Cloudinary accounts are external origins, not assets in Glide's account. */
export function isGlideCloudinaryURL(source: string): boolean {
    try {
        const url = new URL(source);
        return (
            url.hostname.replace(/\.$/, "") === "res.cloudinary.com" &&
            /^\/glide(?:\/|$)/.test(decodeURIComponent(url.pathname).replace(/\/+/g, "/"))
        );
    } catch {
        return false;
    }
}

export function preservedImageURL(source: string, endpoint: string): string {
    try {
        const url = new URL(source);
        const target = new URL(endpoint);
        if (
            url.origin === target.origin &&
            url.pathname === target.pathname &&
            (url.searchParams.has("url") || url.searchParams.has("key"))
        )
            return source;
    } catch {
        /* The request handler validates the source. */
    }
    // Fragments are interpreted by the browser, never sent to the origin.
    // Keep them outside the lookup key (for example a PDF's #page=2).
    const fragment = source.indexOf("#");
    return fragment === -1
        ? `${endpoint}?url=${encodeURIComponent(source)}`
        : `${endpoint}?url=${encodeURIComponent(source.slice(0, fragment))}${source.slice(fragment)}`;
}

export function isPreservedImageEntry(
    value: unknown,
    sourceURL: string,
    projectID: string
): value is PreservedImageEntry {
    try {
        const url = new URL(sourceURL);
        if (!["http:", "https:"].includes(url.protocol) || url.username !== "" || url.password !== "") return false;
    } catch {
        return false;
    }
    if (typeof value !== "object" || value === null) return false;
    const entry = value as Partial<PreservedImageEntry>;
    return (
        entry.sourceURL === sourceURL &&
        [`${projectID}-cloudinary-backup`, `${projectID}-cloudinary-wholesale`].includes(entry.bucket ?? "") &&
        typeof entry.name === "string" &&
        ["originals/", "delivery-snapshots/"].some(prefix => entry.name?.startsWith(prefix)) &&
        !entry.name.split("/").includes("..") &&
        typeof entry.generation === "string" &&
        /^[1-9]\d*$/.test(entry.generation) &&
        typeof entry.size === "number" &&
        Number.isSafeInteger(entry.size) &&
        entry.size > 0 &&
        typeof entry.md5 === "string" &&
        /^[A-Za-z0-9+/]{22}==$/.test(entry.md5) &&
        typeof entry.contentType === "string" &&
        /^(?:(?:image|audio|video)\/[a-z0-9.+-]+|application\/(?:pdf|illustrator|octet-stream|zip)|text\/(?:plain|csv))$/.test(
            entry.contentType
        )
    );
}

export const imageKitFetchBase = "https://ik.imagekit.io/glideapps/fetch";

export function imageKitURL(source: string, transformation: string): string {
    try {
        const url = new URL(source);
        // Emoji origins return SVGs without intrinsic dimensions. Rasterize their
        // previews so existing max-sized logos retain the Cloudinary layout.
        if (
            url.pathname === "/preservedImage" &&
            isGlideEmojiURL(url.searchParams.get("url") ?? "", url.origin) &&
            transformation !== "orig-true" &&
            !/(?:^|[:,])f-/.test(transformation)
        ) {
            // SVG viewboxes can be only 32px wide. Rasterize at the requested
            // size instead of letting the no-enlarge crop leave a blurry icon.
            transformation = transformation.replace(/(^|[:,])c-at_max(?=,|:|$)/g, "$1c-at_max_enlarge");
            transformation = `f-png${transformation === "" ? "" : `,${transformation}`}`;
        }
    } catch (e: unknown) {
        // Preserve the existing URL construction for non-URL inputs.
    }
    return `${imageKitFetchBase}/tr:${transformation}/${encodeURIComponent(source)}`;
}

export interface LegacyImageRecipe {
    readonly source: string;
    readonly transformation: string;
    readonly attachmentFilename?: string;
}

/** Translate only the explicitly audited subset. Unknown effects require an exact saved rendition. */
export function legacyImageRecipe(source: string): LegacyImageRecipe | undefined {
    let url: URL;
    try {
        url = new URL(source);
    } catch {
        return undefined;
    }
    if (url.username !== "" || url.password !== "" || url.hash !== "") return undefined;
    if (url.hostname === "di.glideapps.com" && url.search === "") {
        const wrapped = /^\/[^/]+\/(.+)$/.exec(url.pathname);
        if (wrapped === null) return undefined;
        try {
            const origin = decodeURIComponent(wrapped[1]);
            return /^https?:\/\//.test(origin) ? { source: origin, transformation: "" } : undefined;
        } catch {
            return undefined;
        }
    }
    if (url.hostname === "ik.imagekit.io" && url.pathname.startsWith("/glideapps/fetch/")) {
        const attachmentFilename = url.searchParams.get("ik-attachment-filename") ?? undefined;
        const remainingQuery = new URLSearchParams(url.search);
        remainingQuery.delete("ik-attachment");
        remainingQuery.delete("ik-attachment-filename");
        if (
            url.search !== "" &&
            (url.searchParams.get("ik-attachment") !== "true" ||
                attachmentFilename === undefined ||
                !validAttachmentFilename(attachmentFilename) ||
                url.searchParams.getAll("ik-attachment").length !== 1 ||
                url.searchParams.getAll("ik-attachment-filename").length !== 1 ||
                remainingQuery.toString() !== "")
        )
            return undefined;
        let rest = url.pathname.slice("/glideapps/fetch/".length);
        let transformation = "";
        if (rest.startsWith("tr:")) {
            const slash = rest.indexOf("/");
            if (slash === -1) return undefined;
            transformation = rest.slice(3, slash);
            if (
                !transformation
                    .split(":")
                    .every(step =>
                        step
                            .split(",")
                            .every(token =>
                                /^(?:[wh]-\d+|c-at_max(?:_enlarge)?|fo-(?:face|center)|f-(?:jpg|png|webp|avif)|cm-pad_resize|orig-true)$/.test(
                                    token
                                )
                            )
                    )
            )
                return undefined;
            rest = rest.slice(slash + 1);
        }
        try {
            const origin = decodeURIComponent(rest);
            return /^https?:\/\//.test(origin)
                ? {
                      source: origin,
                      transformation,
                      ...(attachmentFilename === undefined ? {} : { attachmentFilename }),
                  }
                : undefined;
        } catch {
            return undefined;
        }
    }
    if (url.hostname !== "res.cloudinary.com") return undefined;
    const attachment = /^\/glide\/image\/fetch\/fl_attachment(?::([^/,]+))?\/(.+)$/.exec(url.pathname);
    if (attachment !== null && url.search === "") {
        try {
            const origin = /^https?:\/\//.test(attachment[2]) ? attachment[2] : decodeURIComponent(attachment[2]);
            // ImageKit's default attachment header omits the filename. Keep the legacy
            // source basename even when the origin is wrapped by our preservation endpoint.
            const decodedFilename = decodeURIComponent(
                attachment[1] ?? new URL(origin).pathname.split("/").pop() ?? ""
            );
            const attachmentFilename = decodedFilename === "" ? "download" : decodedFilename;
            if (!validAttachmentFilename(attachmentFilename) || !/^https?:\/\//.test(origin)) return undefined;
            return { source: origin, transformation: "orig-true", attachmentFilename };
        } catch {
            return undefined;
        }
    }
    const match = /^\/glide\/image\/(upload|fetch)\/(.+)$/.exec(url.pathname);
    if (match === null) return undefined;
    const parts = match[2].split("/");
    const transformations: string[] = [];
    while (parts.length > 1) {
        const part = parts[0];
        if (
            /^v\d+$/.test(part) ||
            part.startsWith("http") ||
            (!part.includes(",") && !/^(?:t|f|q|w|h|c|g)_/.test(part))
        )
            break;
        const tokens = part.split(",");
        const mapped: string[] = [];
        for (const token of tokens) {
            if (token === "t_integration-logo") mapped.push("w-128,h-128,cm-pad_resize,f-png");
            else if (token === "t_media_lib_thumb") mapped.push("w-150,h-100,c-at_max");
            else if (/^[wh]_\d+$/.test(token)) mapped.push(token.replace("_", "-"));
            else if (token === "c_limit") mapped.push("c-at_max");
            else if (token === "c_fill" || token === "c_lfill") {
                /* ImageKit's default is an aspect-preserving crop. */
            } else if (token === "g_faces") mapped.push("fo-face");
            else if (token === "g_center") mapped.push("fo-center");
            else if (token === "f_auto" || token === "q_auto") {
                /* ImageKit optimizes automatically. */
            } else if (/^f_(jpg|png|webp|avif)$/.test(token)) mapped.push(token.replace("_", "-"));
            else return undefined;
        }
        transformations.push(mapped.join(","));
        parts.shift();
    }
    if (match[1] === "upload") {
        if (transformations.length === 0 || url.search !== "") return undefined;
        return {
            source: `https://res.cloudinary.com/glide/image/upload/${parts.join("/")}`,
            transformation: transformations.filter(Boolean).join(":"),
        };
    }
    let origin = parts.join("/");
    try {
        if (!/^https?:\/\//.test(origin)) origin = decodeURIComponent(origin);
    } catch {
        return undefined;
    }
    if (!/^https?:\/\//.test(origin) || url.search !== "") return undefined;
    return { source: origin, transformation: transformations.filter(Boolean).join(":") };
}

/** Keep static metadata on its original source; choose a provider at rendering time. */
export function glideAssetSource(path: string): string {
    return `https://res.cloudinary.com/glide/${path}`;
}

function validAttachmentFilename(filename: string): boolean {
    return filename.length > 0 && filename.length <= 200 && !/[\x00-\x1f\x7f/\\]/.test(filename);
}
